import type { GatewayBrowserClient } from "../gateway.ts";

/** Summary of a multiagent configuration/template derived from the fleet graph. */
export type MultiagentTemplateSummary = {
  id: string;
  name: string;
  description: string;
  agentCount: number;
  /** Agent IDs included in this configuration */
  agents: string[];
  /** Architecture type (e.g., 'hierarchical', 'sequential', 'parallel', 'custom') */
  architecture?: string;
};

/** Full multiagent template with routing configuration */
export type MultiagentTemplate = MultiagentTemplateSummary & {
  bindings: Array<{
    agentId: string;
    keywords: string[];
    priority: number;
    fallback: boolean;
  }>;
};

/** Extract workforce bindings from config to build templates */
function extractBindingsFromConfig(config: Record<string, unknown>): Array<{
  agentId: string;
  keywords: string[];
  priority: number;
  fallback: boolean;
}> {
  const workforce = config.workforce as Record<string, unknown> | undefined;
  if (!workforce || !Array.isArray(workforce.bindings)) {
    return [];
  }
  return (workforce.bindings as Record<string, unknown>[]).map((binding) => {
    const match = binding.match as Record<string, unknown> | undefined;
    return {
      agentId: String(binding.agentId ?? ""),
      keywords: Array.isArray(match?.keywords) ? match.keywords.map((k) => String(k)) : [],
      priority: typeof binding.priority === "number" ? binding.priority : 50,
      fallback: binding.fallback === true,
    };
  });
}

/** Extract agent IDs from fleet graph nodes */
function extractAgentIds(config: Record<string, unknown>): string[] {
  const agents = config.agents as { list?: Array<{ id?: string }> } | undefined;
  if (!agents?.list) {
    return [];
  }
  return agents.list.map((entry) => entry.id?.trim()).filter((id): id is string => Boolean(id));
}

/**
 * Load multiagent templates from the gateway config.
 * These are derived from the workforce.bindings configuration.
 */
export async function loadMultiagentTemplates(
  client: GatewayBrowserClient | null,
  connected: boolean,
): Promise<MultiagentTemplateSummary[]> {
  if (!client || !connected) {
    return [];
  }

  try {
    const snapshot = await client.request<{
      config?: Record<string, unknown>;
    }>("config.get", {});

    const config = snapshot.config;
    if (!config) {
      return [];
    }

    const bindings = extractBindingsFromConfig(config);
    const agentIds = extractAgentIds(config);

    // Build templates based on workforce bindings
    const templates: MultiagentTemplateSummary[] = [];

    // Default/empty multiagent configuration
    templates.push({
      id: "default",
      name: "Default Fleet",
      description: "Use all configured agents with default routing",
      agentCount: agentIds.length,
      agents: agentIds,
    });

    // Individual agent templates
    for (const agentId of agentIds) {
      templates.push({
        id: `agent:${agentId}`,
        name: `Agent: ${agentId}`,
        description: `Single agent: ${agentId}`,
        agentCount: 1,
        agents: [agentId],
      });
    }

    // Workforce-based templates
    const agentsWithBindings = new Set(bindings.map((b) => b.agentId));
    for (const agentId of agentsWithBindings) {
      const agentBindings = bindings.filter((b) => b.agentId === agentId);
      const keywords = agentBindings.flatMap((b) => b.keywords).slice(0, 3);
      templates.push({
        id: `workforce:${agentId}`,
        name: `Workforce: ${agentId}`,
        description: `Routes to ${agentId}${keywords.length > 0 ? ` (${keywords.join(", ")})` : ""}`,
        agentCount: 1,
        agents: [agentId],
      });
    }

    return templates;
  } catch {
    return [];
  }
}

/**
 * Select a multiagent template and return the effective agent IDs.
 */
/** State interface for multiagent templates */
export type MultiagentTemplatesState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  multiagentTemplatesLoading: boolean;
  multiagentTemplatesError: string | null;
  multiagentTemplates: MultiagentTemplateSummary[];
  selectedMultiagentTemplateId: string | null;
};

/**
 * Handle template selection - updates state and triggers any needed effects
 */
export function selectMultiagentTemplate(
  state: MultiagentTemplatesState,
  templateId: string | null,
): void {
  if ("selectedMultiagentTemplateId" in state) {
    (state as { selectedMultiagentTemplateId: string | null }).selectedMultiagentTemplateId =
      templateId;
  } else if ("selectedTemplateId" in state) {
    (state as { selectedTemplateId: string | null }).selectedTemplateId = templateId;
  }
}

export function resolveMultiagentTemplateAgents(
  templates: MultiagentTemplateSummary[],
  templateId: string | null,
): string[] {
  if (!templateId) {
    return [];
  }

  const template = templates.find((t) => t.id === templateId);
  return template?.agents ?? [];
}
